import React, { useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { RootState } from "../../../types/State.interface";
import styles from "./GridBlock.module.scss";
import IdeaPhotoCard from "../Cards/IdeaPhotoCard/IdeaPhotoCard";
import { getLayoutConfig } from "../../../utils/mediaCards.utils";
import { Idea } from "../../../types/Idea.interface";
import {
  useIsDesktop,
  useIsTablet,
  useIsMobile,
} from "../../../hooks/useMedia";

interface GridBlockProps {
  normalCards: Idea[];
  mediaCards: Idea[];
  type: "top" | "bottom";
  onCardClick?: (idea: any) => void;
  onAuthorClick?: (author: any) => void;
  onTagClick?: (tag: any) => void;
  onLike?: (idea: any) => void;
  onRenderedCards?: (cards: Idea[]) => void;
}

export const GridBlock: React.FC<GridBlockProps> = ({
  normalCards,
  mediaCards,
  type,
  onCardClick,
  onAuthorClick,
  onTagClick,
  onLike,
  onRenderedCards,
}) => {
  const user = useSelector((state: RootState) => state.user.userDetails);
  const isTablet = useIsTablet();
  const isDesktop = useIsDesktop();
  const isMobile = useIsMobile();

  const { mediaIndex, squareIndexes, normalRequired } = getLayoutConfig(
    type,
    isDesktop,
    isTablet
  );

  const hasMedia = mediaCards.length > 0;
  const hasNormal = normalCards.length > 0;

  const isFull =
    normalCards.length >= normalRequired - (isDesktop ? 3 : 2) && hasMedia;

  const onlyNormal = hasNormal && !hasMedia;

  const onlyMedia = !hasNormal && hasMedia;

  const splitGridView =
    hasMedia &&
    ((isDesktop && normalCards.length > 0 && normalCards.length <= 4) ||
      (isTablet && normalCards.length > 0 && normalCards.length <= 3));

  const totalCards = useMemo(() => {
    const cards = [...normalCards];

    if (cards.length > 0) {
      for (let i = mediaCards.length - 1; i >= 0; i--) {
        cards.splice(mediaIndex + i, 0, mediaCards[i]);
      }
    } else {
      for (let i = 0; i < mediaCards.length; i++) {
        cards.push(mediaCards[i]);
      }
    }

    return cards;
  }, [normalCards, mediaCards, mediaIndex]);

  useEffect(() => {
    if (!isMobile && (isFull || onlyNormal || onlyMedia)) {
      onRenderedCards?.(totalCards);
    } else if (!isMobile && splitGridView) {
      onRenderedCards?.([...normalCards, ...mediaCards]);
    } else if (isMobile) {
      const mobileCards: Idea[] = [];
      const normals = [...normalCards];
      const medias = [...mediaCards];

      const normalTarget = 5;
      const mediaTarget = 1;

      const normalsTaken = normals.splice(0, normalTarget);
      const mediasTaken = medias.splice(0, mediaTarget);

      mobileCards.push(...normalsTaken, ...mediasTaken);

      const total = mobileCards.length;
      if (total < 6) {
        const spaceLeft = 6 - total;
        mobileCards.push(...normals.splice(0, spaceLeft));
        if (mobileCards.length < 6) {
          mobileCards.push(...medias.splice(0, 6 - mobileCards.length));
        }
      }

      onRenderedCards?.(mobileCards);
    }
  }, [
    normalCards,
    mediaCards,
    totalCards,
    isMobile,
    isFull,
    onlyNormal,
    onlyMedia,
    splitGridView,
    onRenderedCards,
  ]);

  const renderFullGrid = () =>
    totalCards.map((idea, index) => {
      const isSquare = !idea.isMediaCard && squareIndexes.includes(index);
      return (
        <IdeaPhotoCard
          key={idea.id || index}
          idea={idea}
          user={user}
          isMediaCard={idea.isMediaCard}
          onCardClick={onCardClick}
          onAuthorClick={onAuthorClick}
          onTagClick={onTagClick}
          onLike={onLike}
          className={classNames(styles.card, {
            [styles.square]: isSquare,
            [styles.media]: idea.isMediaCard,
          })}
          isGridView
        />
      );
    });

  const renderSplitGrid = () => (
    <>
      <div className={classNames(styles["only-normal"])}>
        {normalCards.map((idea, index) => (
          <IdeaPhotoCard
            key={idea.id || index}
            idea={idea}
            user={user}
            isMediaCard={false}
            onCardClick={onCardClick}
            onAuthorClick={onAuthorClick}
            onTagClick={onTagClick}
            onLike={onLike}
            className={classNames(styles.card)}
            isGridView
          />
        ))}
      </div>
      <div className={classNames(styles["only-media"])}>
        {mediaCards.map((idea, index) => (
          <IdeaPhotoCard
            key={idea.id || index}
            idea={idea}
            user={user}
            isMediaCard
            onCardClick={onCardClick}
            onAuthorClick={onAuthorClick}
            onTagClick={onTagClick}
            onLike={onLike}
            className={classNames(styles.card, styles.media)}
            isGridView
          />
        ))}
      </div>
    </>
  );

  const renderMobileGrid = () => {
    const mobileCards: Idea[] = [];
    const normals = [...normalCards];
    const medias = [...mediaCards];

    // Приоритет: 5 нормальных и 1 медиа
    const normalTarget = 5;
    const mediaTarget = 1;

    const normalsTaken = normals.splice(0, normalTarget);
    const mediasTaken = medias.splice(0, mediaTarget);

    mobileCards.push(...normalsTaken, ...mediasTaken);

    // Если всего меньше 6 — добираем из остатка
    const total = mobileCards.length;
    if (total < 6) {
      const spaceLeft = 6 - total;
      mobileCards.push(...normals.splice(0, spaceLeft));
      if (mobileCards.length < 6) {
        mobileCards.push(...medias.splice(0, 6 - mobileCards.length));
      }
    }

    return (
      <div className={styles.mobile}>
        {mobileCards.map((idea, index) => (
          <IdeaPhotoCard
            key={idea.id || index}
            idea={idea}
            user={user}
            isMediaCard={idea.isMediaCard}
            onCardClick={onCardClick}
            onAuthorClick={onAuthorClick}
            onTagClick={onTagClick}
            onLike={onLike}
            className={classNames(styles.card, {
              [styles.media]: idea.isMediaCard,
            })}
            isGridView
          />
        ))}
      </div>
    );
  };

  if (isMobile) return renderMobileGrid();

  return (
    <div
      className={classNames(styles[type], {
        [styles.full]: isFull,
        [styles["only-normal"]]: onlyNormal,
        [styles["only-media"]]: onlyMedia,
        [styles.grid]: !splitGridView,
        [styles["split-grid"]]: splitGridView,
      })}
    >
      {(isFull || onlyNormal || onlyMedia) && renderFullGrid()}
      {splitGridView && renderSplitGrid()}
    </div>
  );
};
